/**
 * Polls the DOM for an element matching `query`, invoking `callback` once found.
 * If the element is not found within `timeout` ms, `callback` is called with `null`.
 * @param query - CSS selector for the target element.
 * @param callback - Invoked with the element when found, or `null` on timeout.
 * @param interval - Polling interval in ms. Defaults to `50`.
 * @param timeout - Max time to poll in ms before giving up. Defaults to `1000`.
 * @returns The interval ID, allowing the caller to cancel polling early.
 */
export const pollForElement = (
  query: string,
  callback: (element: HTMLElement | null) => void,
  interval = 50,
  timeout = 1000
): ReturnType<typeof setInterval> => {
  const deadline = Date.now() + timeout;
  const id = setInterval(() => {
    const el = document.querySelector<HTMLElement>(query);
    if (el) {
      clearInterval(id);
      callback(el);
    } else if (Date.now() >= deadline) {
      clearInterval(id);
      callback(null);
    }
  }, interval);
  return id;
};
